<script setup lang="ts">
  import { formatNumeral } from 'cleave-zen'
  import { computed, ref, useId, useTemplateRef, watchEffect } from 'vue'

  const props = defineProps<{
    id?: string,
    min?: number,
    label?: string,
    required?: boolean,
    disabled?: boolean,
    placeholder?: string,
    error?: string[],
    prefix?: string,
    suffix?: string,
  }>()

  const modelValue = defineModel<number>()
  const isFocus = ref(false)
  const inputNumber = useTemplateRef('inputNumber')
  const defaultId = useId()
  const inputId = computed(() => props.id ?? defaultId)
  const onChange = (event: Event | string) => {
    let data = '0'
    if (typeof event === 'string') {
      data = event
    } else {
      const target = event.target as HTMLInputElement
      data = target.value
    }
    const convert = formatNumeral(data, {
      delimiter: '.',
      numeralDecimalMark: ',',
      numeralDecimalScale: 1,
      numeralPositiveOnly: true,
    })

    if (convert) {
      if (inputNumber.value) {
        inputNumber.value.value = convert
      }
      modelValue.value = Number(
        convert
          .replace('.', '')
          .replace(',', '.'),
      )
      return
    }

    if (inputNumber.value) {
      inputNumber.value.value = '0'
    }
    modelValue.value = 0
  }

  watchEffect(() => {
    if (isFocus.value) {
      return
    }
    onChange(
      String(modelValue.value).replace('.', ','), // handle when decimal 0.1 to 0,1
    )
  })
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>
    <div class="relative flex items-center">
      <input
        :id="inputId"
        ref="inputNumber"
        v-select-on-focus
        :disabled="disabled"
        class="form-input"
        inputmode="numeric"
        type="text"
        autocomplete="off"
        :class="{ '!pl-10': prefix, '!pr-10': suffix }"
        @focus="isFocus = true"
        @blur="isFocus = false"
        @input="onChange">
      <span
        v-if="prefix"
        class="prefix">
        {{ prefix }}
      </span>
      <span
        v-if="suffix"
        class="suffix">
        {{ suffix }}
      </span>
    </div>
    <app-error-msg :msg="error" />
  </div>
</template>

<style lang="postcss" scoped>
  .prefix {
    @apply absolute left-4 text-gray-400 pointer-events-none text-sm z-10;
  }
  .input-has-text .prefix.suffix {
    @apply inline;
  }

  .suffix {
    @apply absolute right-4 text-gray-400 pointer-events-none text-sm z-10;
  }
</style>
